/**
 * CustomerDetailPage — `/admin/customers/:id`.
 *
 * Customer-360 surface for the staff console. Stitches together the
 * customer detail, address book, mail items, service-desk cases, and
 * NIPOST Pay wallet (+ recent transactions) for a single customer.
 *
 * Backends consumed:
 *   - GET /v1/customers/:id                       — service-customer
 *   - GET /v1/customers/:id/addresses             — service-customer
 *   - GET /v1/mail-items?customerId=:id           — service-mail
 *   - GET /v1/service-desk/cases?customerId=:id   — service-service-desk
 *   - GET /v1/wallet/wallets?customerId=:id       — service-financial-services
 *   - GET /v1/wallet/wallets/:walletId/transactions
 *
 * Each tab loads independently — a 4xx on one tab degrades that tab only,
 * the others remain usable.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Plus, ShieldCheck } from 'lucide-react';

import { ApiError } from '../../../lib/api/client';
import {
  getCustomerById,
  listCustomerAddresses,
  type CustomerAddress,
} from '../../../lib/api/customers';
import {
  formatNgn,
  listWallets,
  listWalletTransactions,
  recordWalletCredit,
  type Wallet,
  type WalletTransaction,
} from '../../../lib/api/financial-services';
import {
  listMailItems,
  type MailItemSummary,
} from '../../../lib/api/mail-items';
import {
  listServiceCases,
  type ServiceDeskCase,
} from '../../../lib/api/service-desk';
import { DeferredNotice, StatusBadge } from '../../shared/StaffUI';

type TabKey = 'profile' | 'mail' | 'cases' | 'wallet';

const TABS: readonly { readonly key: TabKey; readonly label: string }[] = [
  { key: 'profile', label: 'Profile' },
  { key: 'mail', label: 'Mail items' },
  { key: 'cases', label: 'Service desk cases' },
  { key: 'wallet', label: 'Wallet & transactions' },
];

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' });
}

function gradeBadge(grade: string): JSX.Element {
  const tone =
    grade === 'REGISTERED'
      ? 'bg-nipost-green-light text-nipost-green'
      : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tone}`}>
      {grade.replace(/_/g, ' ')}
    </span>
  );
}

export function CustomerDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [tab, setTab] = useState<TabKey>('profile');

  const customer = useQuery({
    queryKey: ['admin', 'customers', 'detail', id] as const,
    queryFn: () => getCustomerById(id),
    retry: false,
    enabled: id.length > 0,
    staleTime: 60_000,
  });

  if (!id) {
    return (
      <div className="text-sm text-red-600">Missing customer id in the URL.</div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/customers"
        className="inline-flex items-center gap-1 text-xs text-muted hover:text-gray-900"
        data-testid="cust-back-link"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Customers
      </Link>

      {customer.isPending ? (
        <p className="text-sm text-muted">Loading customer…</p>
      ) : customer.isError ? (
        <DeferredNotice title="Customer not loaded">
          <p>{(customer.error as Error).message}</p>
        </DeferredNotice>
      ) : (
        <>
          {/* Header */}
          <div className="bg-white border border-border rounded-xl p-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div className="space-y-1">
                <h1 className="text-2xl font-bold text-gray-900" data-testid="cust-fullname">
                  {customer.data.fullName}
                </h1>
                <p className="text-xs font-mono text-muted">{customer.data.customerNumber}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {gradeBadge(customer.data.grade)}
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                  <ShieldCheck className="w-3 h-3" />
                  KYC {customer.data.kycLevel.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
            <dl className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 text-sm">
              <div>
                <dt className="text-[10px] uppercase text-muted font-semibold">Phone</dt>
                <dd className="font-mono">{customer.data.primaryPhone}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase text-muted font-semibold">Email</dt>
                <dd>{customer.data.primaryEmail ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-[10px] uppercase text-muted font-semibold">Captured by</dt>
                <dd>Operator #{customer.data.capturedByOperatorId}</dd>
              </div>
            </dl>
          </div>

          {/* Tabs */}
          <div role="tablist" className="flex items-center gap-1 border-b border-border">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t.key
                    ? 'border-nipost-green text-nipost-green'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'profile' ? <ProfileTab customerId={id} customer={customer.data} /> : null}
          {tab === 'mail' ? <MailTab customerId={id} /> : null}
          {tab === 'cases' ? <CasesTab customerId={id} /> : null}
          {tab === 'wallet' ? <WalletTab customerId={id} /> : null}
        </>
      )}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile tab — full detail + address book.
// ---------------------------------------------------------------------------

interface ProfileTabProps {
  readonly customerId: string;
  readonly customer: Awaited<ReturnType<typeof getCustomerById>>;
}

function ProfileTab({ customerId, customer }: ProfileTabProps): JSX.Element {
  const addresses = useQuery({
    queryKey: ['admin', 'customers', 'addresses', customerId] as const,
    queryFn: () => listCustomerAddresses(customerId),
    retry: false,
    staleTime: 60_000,
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-[10px] uppercase text-muted font-semibold">Display name</dt>
            <dd>{customer.displayName ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted font-semibold">KYC tier</dt>
            <dd>{customer.kycLevel}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted font-semibold">Date of birth</dt>
            <dd>{customer.dateOfBirth ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted font-semibold">Registered</dt>
            <dd>{formatDate(customer.createdAt)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted font-semibold">Last updated</dt>
            <dd>{formatDate(customer.updatedAt)}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase text-muted font-semibold">Status</dt>
            <dd>{customer.deactivatedAt ? 'Deactivated' : 'Active'}</dd>
          </div>
        </dl>
      </div>

      <div className="bg-white border border-border rounded-xl p-4 space-y-3">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-muted" />
          Address book
        </h2>
        {addresses.isPending ? (
          <p className="text-sm text-muted">Loading addresses…</p>
        ) : addresses.isError ? (
          <p className="text-xs text-red-600">{(addresses.error as Error).message}</p>
        ) : addresses.data.length === 0 ? (
          <p className="text-sm text-muted">No addresses on file.</p>
        ) : (
          <ul className="space-y-2">
            {addresses.data.map((a: CustomerAddress) => (
              <li
                key={a.id}
                className="border border-border rounded-lg p-3 text-xs space-y-1"
              >
                <div className="flex items-center gap-2">
                  <span className="font-semibold uppercase">{a.label}</span>
                  {a.isPrimary ? (
                    <span className="px-1.5 py-0.5 bg-nipost-green-light text-nipost-green rounded">
                      Primary
                    </span>
                  ) : null}
                </div>
                <p>{a.line1}{a.line2 ? `, ${a.line2}` : ''}</p>
                <p className="text-muted">
                  {a.city}, {a.state} {a.postalCode ?? ''} · {a.countryCode}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mail items tab — paginated mail items filtered by customerId.
// ---------------------------------------------------------------------------

function MailTab({ customerId }: { readonly customerId: string }): JSX.Element {
  const items = useQuery({
    queryKey: ['admin', 'customers', 'mail-items', customerId] as const,
    queryFn: () => listMailItems({ customerId, pageSize: 20 }),
    retry: false,
    staleTime: 30_000,
  });

  if (items.isPending) return <p className="text-sm text-muted">Loading mail items…</p>;
  if (items.isError) {
    return (
      <DeferredNotice title="Mail items unavailable">
        <p>{(items.error as Error).message}</p>
      </DeferredNotice>
    );
  }
  if (items.data.items.length === 0) {
    return (
      <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
        No mail items registered for this customer yet.
      </p>
    );
  }

  return (
    <div className="bg-white border border-border rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid="mail-table">
        <thead className="bg-gray-50 border-b border-border">
          <tr>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Tracking #</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Status</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Service</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Registered</th>
            <th className="text-right px-4 py-2 text-[10px] uppercase text-muted">Amount</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.data.items.map((it: MailItemSummary) => {
            // pricing.amountNgn lives only on the detail projection — the
            // summary list returns lean fields. We still render '—' as a
            // placeholder so the column stays aligned.
            const pricingAmount =
              'pricing' in it && it.pricing && typeof (it as { pricing: { amountNgn?: string } }).pricing === 'object'
                ? (it as { pricing: { amountNgn?: string } }).pricing.amountNgn
                : undefined;
            return (
              <tr key={it.id} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-xs font-mono">
                  <Link
                    to={`/mail/items/${it.id}`}
                    className="text-nipost-green hover:underline"
                  >
                    {it.trackingNumber}
                  </Link>
                </td>
                <td className="px-4 py-2"><StatusBadge status={it.status} /></td>
                <td className="px-4 py-2 text-xs">{it.serviceClass.replace(/_/g, ' ')}</td>
                <td className="px-4 py-2 text-xs text-muted">{formatDate(it.registeredAt)}</td>
                <td className="px-4 py-2 text-xs text-right">
                  {pricingAmount ? `₦${pricingAmount}` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Service desk cases tab.
// ---------------------------------------------------------------------------

function CasesTab({ customerId }: { readonly customerId: string }): JSX.Element {
  const cases = useQuery({
    queryKey: ['admin', 'customers', 'cases', customerId] as const,
    queryFn: () => listServiceCases({ customerId, pageSize: 20 }),
    retry: false,
    staleTime: 30_000,
  });

  if (cases.isPending) return <p className="text-sm text-muted">Loading cases…</p>;
  if (cases.isError) {
    return (
      <DeferredNotice title="Service desk unavailable">
        <p>{(cases.error as Error).message}</p>
      </DeferredNotice>
    );
  }
  if (cases.data.items.length === 0) {
    return (
      <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
        No service desk cases for this customer.
      </p>
    );
  }

  return (
    <div className="bg-white border border-border rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid="cases-table">
        <thead className="bg-gray-50 border-b border-border">
          <tr>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Case #</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Status</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Priority</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Form</th>
            <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Opened</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {cases.data.items.map((c: ServiceDeskCase) => (
            <tr key={c.id} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-xs font-mono">
                <Link
                  to={`/customer-service/complaints/${c.id}`}
                  className="text-nipost-green hover:underline"
                >
                  {c.caseNumber}
                </Link>
              </td>
              <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
              <td className="px-4 py-2 text-xs">{c.priority}</td>
              <td className="px-4 py-2 text-xs">{c.formKey}</td>
              <td className="px-4 py-2 text-xs text-muted">{formatDate(c.openedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wallet & transactions tab.
// ---------------------------------------------------------------------------

function WalletTab({ customerId }: { readonly customerId: string }): JSX.Element {
  const [topUpOpen, setTopUpOpen] = useState(false);
  const wallets = useQuery({
    queryKey: ['admin', 'customers', 'wallets', customerId] as const,
    queryFn: () => listWallets({ customerId, pageSize: 5 }),
    retry: false,
    staleTime: 30_000,
  });

  const wallet: Wallet | undefined = wallets.data?.items[0];
  const walletId = wallet?.id;

  const txns = useQuery({
    queryKey: ['admin', 'customers', 'wallet-txns', walletId] as const,
    queryFn: () => listWalletTransactions(walletId ?? '', { pageSize: 10 }),
    retry: false,
    enabled: walletId !== undefined,
    staleTime: 30_000,
  });

  if (wallets.isPending) return <p className="text-sm text-muted">Loading wallet…</p>;

  // 404 / no wallet → graceful empty.
  const noWallet =
    !wallets.isError &&
    (wallets.data === undefined || wallets.data.items.length === 0);

  if (
    wallets.isError &&
    wallets.error instanceof ApiError &&
    wallets.error.status === 404
  ) {
    return (
      <p
        className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center"
        data-testid="wallet-empty"
      >
        No NIPOST Pay wallet activated for this customer.
      </p>
    );
  }

  if (wallets.isError) {
    return (
      <DeferredNotice title="Wallet service unavailable">
        <p>{(wallets.error as Error).message}</p>
      </DeferredNotice>
    );
  }

  if (noWallet || !wallet) {
    return (
      <p
        className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center"
        data-testid="wallet-empty"
      >
        No NIPOST Pay wallet activated for this customer.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-border rounded-xl p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
            <div>
              <p className="text-[10px] uppercase text-muted font-semibold">Wallet #</p>
              <p className="text-sm font-mono">{wallet.walletNumber}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted font-semibold">Balance</p>
              <p className="text-lg font-bold text-gray-900">{formatNgn(wallet.balanceCached)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted font-semibold">Tier</p>
              <p className="text-sm">{wallet.kycTier}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase text-muted font-semibold">Status</p>
              <StatusBadge status={wallet.status} />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setTopUpOpen(true)}
            disabled={wallet.status !== 'ACTIVE'}
            data-testid="wallet-topup-button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-nipost-green text-white hover:bg-nipost-green-dark disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            title={wallet.status === 'ACTIVE' ? 'Top up this wallet on the customer’s behalf' : `Wallet is ${wallet.status} — cannot top up`}
          >
            <Plus className="w-3.5 h-3.5" /> Top up wallet
          </button>
        </div>
      </div>

      {topUpOpen ? (
        <TopUpWalletModal
          wallet={wallet}
          customerId={customerId}
          onClose={() => setTopUpOpen(false)}
        />
      ) : null}

      <div className="bg-white border border-border rounded-xl overflow-x-auto">
        <h2 className="text-sm font-semibold text-gray-900 px-4 pt-4">Recent transactions</h2>
        {txns.isPending ? (
          <p className="text-sm text-muted p-4">Loading transactions…</p>
        ) : txns.isError ? (
          <p className="text-xs text-red-600 p-4">{(txns.error as Error).message}</p>
        ) : txns.data.items.length === 0 ? (
          <p className="text-sm text-muted p-4">No transactions yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-border">
              <tr>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Txn #</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Side</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Action</th>
                <th className="text-right px-4 py-2 text-[10px] uppercase text-muted">Amount</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">Status</th>
                <th className="text-left px-4 py-2 text-[10px] uppercase text-muted">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {txns.data.items.map((t: WalletTransaction) => (
                <tr key={t.id}>
                  <td className="px-4 py-2 text-xs font-mono">{t.txnNumber}</td>
                  <td className="px-4 py-2 text-xs">{t.side}</td>
                  <td className="px-4 py-2 text-xs">{t.action}</td>
                  <td className="px-4 py-2 text-xs text-right">{formatNgn(t.amount)}</td>
                  <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-2 text-xs text-muted">{formatDate(t.initiatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Top-up wallet modal — staff side cash-in.
// Posts a CREDIT to the customer's wallet via service-financial-services.
// Permission required on the operator's role: WalletTransaction/credit.
// ---------------------------------------------------------------------------

function TopUpWalletModal({
  wallet,
  customerId,
  onClose,
}: {
  readonly wallet: Wallet;
  readonly customerId: string;
  readonly onClose: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState<string>('');
  const [counterRef, setCounterRef] = useState<string>('');
  const queryClient = useQueryClient();

  const credit = useMutation({
    mutationFn: () => {
      return recordWalletCredit({
        walletId: wallet.id,
        action: 'TOPUP_TRANSFER',
        amount: amount.trim(),
        currency: wallet.currency,
        counterRef: counterRef.trim() === '' ? undefined : counterRef.trim(),
        idempotencyKey: `staff-topup-${wallet.id}-${Date.now()}`,
        metadata: { source: 'web-staff:customer-detail:top-up' },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'customers', 'wallets', customerId] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'customers', 'wallet-txns', wallet.id] });
      onClose();
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = amount.trim();
    // eslint-disable-next-line upmp/no-number-money -- amount-input gate, not money math; the wire body is the original decimal string
    if (trimmed === '' || Number.isNaN(Number(trimmed)) || Number(trimmed) <= 0) return;
    credit.mutate();
  }

  const errorMessage = credit.isError
    ? credit.error instanceof ApiError && credit.error.status === 403
      ? 'You do not have the WalletTransaction/credit permission. Ask an admin to grant it.'
      : credit.error instanceof ApiError && credit.error.status === 422
        ? `Top-up rejected: ${credit.error.message}. (Common cause: KYC tier daily-inflow cap.)`
        : (credit.error as Error).message
    : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Top up customer wallet"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-xl border border-border w-full max-w-md p-5 space-y-4"
        data-testid="wallet-topup-modal"
      >
        <div>
          <h2 className="text-base font-semibold text-gray-900">Top up wallet</h2>
          <p className="text-xs text-muted mt-1">
            Credits the customer’s NIPOST Pay wallet. Use for cash-in at the counter or to record an inbound bank transfer.
          </p>
        </div>
        <div className="bg-gray-50 border border-border rounded-lg p-3 grid grid-cols-3 gap-3 text-xs">
          <div>
            <p className="text-[10px] uppercase text-muted font-semibold">Wallet</p>
            <p className="font-mono">{wallet.walletNumber}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-muted font-semibold">Currency</p>
            <p>{wallet.currency}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-muted font-semibold">Current balance</p>
            <p className="font-semibold">{formatNgn(wallet.balanceCached)}</p>
          </div>
        </div>
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">Amount ({wallet.currency})</span>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 5000.00"
            required
            data-testid="wallet-topup-amount"
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg text-sm font-mono"
          />
          <span className="text-[10px] text-muted mt-1 block">Decimal up to 2dp. The financial-audit guard rejects more than 2dp.</span>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-gray-700">Counter reference (optional)</span>
          <input
            type="text"
            value={counterRef}
            onChange={(e) => setCounterRef(e.target.value)}
            placeholder="e.g. cash receipt #4231 or bank txn ref"
            maxLength={120}
            data-testid="wallet-topup-counterref"
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg text-sm"
          />
        </label>
        {errorMessage ? (
          <p className="text-xs text-red-600" data-testid="wallet-topup-error">{errorMessage}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={credit.isPending}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-border hover:bg-muted/10 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              credit.isPending ||
              amount.trim() === '' ||
              // eslint-disable-next-line upmp/no-number-money -- amount-input gate, not money math; the wire body is the original decimal string
              Number(amount) <= 0
            }
            data-testid="wallet-topup-submit"
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-nipost-green text-white hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {credit.isPending ? 'Crediting…' : 'Credit wallet'}
          </button>
        </div>
      </form>
    </div>
  );
}
